import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCrown, faCheckCircle, faTimesCircle, faCalendar,
  faArrowRight, faGraduationCap, faFilter, faSearch,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import { getExamUrl } from '../utils/slugify';
import SEO from '../components/SEO';

interface Subscription {
  id: number;
  exam_id: number;
  exam_name: string;
  exam_description: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  created_at: string;
  amount_paid?: number;
  currency?: string;
  payment_id?: string;
}

type FilterType = 'all' | 'active' | 'expired';

const MySubscriptions: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const response = await api.get('/subscriptions');
      setSubscriptions(response.data.subscriptions || []);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getSubscriptionStatus = (subscription: Subscription) => {
    const now = new Date();
    const endDate = new Date(subscription.end_date);
    
    if (!subscription.is_active) {
      return { status: 'inactive', label: 'Inactive', color: 'muted' };
    }
    
    if (endDate < now) {
      return { status: 'expired', label: 'Expired', color: 'danger' };
    }
    
    const daysRemaining = getDaysRemaining(subscription.end_date);
    if (daysRemaining <= 7) {
      return { status: 'expiring', label: 'Expiring Soon', color: 'warning' };
    }
    
    return { status: 'active', label: 'Active', color: 'success' };
  };

  const statusBadgeStyle = (color: string): React.CSSProperties => {
    if (color === 'success') {
      return { background: 'var(--gp-success-soft)', color: 'var(--gp-success)', border: '1px solid var(--gp-success)' };
    }
    if (color === 'warning') {
      return { background: 'var(--gp-accent-soft)', color: 'var(--gp-accent)', border: '1px solid var(--gp-accent)' };
    }
    if (color === 'danger') {
      return { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' };
    }
    return { background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)', border: '1px solid var(--gp-border)' };
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    // Filter by status
    const status = getSubscriptionStatus(sub);
    if (filter === 'active' && status.status !== 'active') return false;
    if (filter === 'expired' && status.status !== 'expired' && status.status !== 'inactive') return false;
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        sub.exam_name.toLowerCase().includes(query) ||
        sub.exam_description?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const activeCount = subscriptions.filter(sub => {
    const status = getSubscriptionStatus(sub);
    return status.status === 'active';
  }).length;

  const expiredCount = subscriptions.filter(sub => {
    const status = getSubscriptionStatus(sub);
    return status.status === 'expired' || status.status === 'inactive';
  }).length;

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div className="text-center">
          <div className="gp-spinner mb-3" aria-hidden="true" />
          <p className="gp-text-muted text-sm">Loading subscriptions...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="My Subscriptions - GlobalPreps"
        description="View and manage your active subscriptions"
        url="/subscriptions"
      />
      <div className="gp-page py-6">
        <div className="gp-container">
          <div className="gp-page-header">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="gp-icon-tile-lg" style={{ background: 'linear-gradient(135deg, var(--gp-accent) 0%, #d97706 100%)' }}>
                  <FontAwesomeIcon icon={faCrown} className="text-lg" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h1 className="gp-page-title">My Subscriptions</h1>
                  <p className="gp-page-subtitle">Manage your premium exam access</p>
                </div>
              </div>
              <Link to="/certifications" className="gp-btn-primary hidden sm:inline-flex">
                <span>Browse Exams</span>
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="gp-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs gp-text-muted">Total Subscriptions</p>
                    <p className="text-xl font-bold mt-0.5" style={{ color: 'var(--gp-ink)' }}>
                      {subscriptions.length}
                    </p>
                  </div>
                  <span className="gp-icon-tile">
                    <FontAwesomeIcon icon={faGraduationCap} aria-hidden="true" />
                  </span>
                </div>
              </div>
              <div className="gp-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs gp-text-muted">Active</p>
                    <p className="text-xl font-bold mt-0.5" style={{ color: 'var(--gp-success)' }}>
                      {activeCount}
                    </p>
                  </div>
                  <span className="gp-icon-tile" style={{ background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }}>
                    <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                  </span>
                </div>
              </div>
              <div className="gp-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs gp-text-muted">Expired</p>
                    <p className="text-xl font-bold mt-0.5" style={{ color: '#b91c1c' }}>
                      {expiredCount}
                    </p>
                  </div>
                  <span className="gp-icon-tile" style={{ background: '#fef2f2', color: '#b91c1c' }}>
                    <FontAwesomeIcon icon={faTimesCircle} aria-hidden="true" />
                  </span>
                </div>
              </div>
            </div>

            <div className="gp-card mb-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-sm gp-text-muted"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    placeholder="Search subscriptions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="gp-search-input"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <FontAwesomeIcon icon={faFilter} className="gp-text-muted text-sm" aria-hidden="true" />
                  <button
                    type="button"
                    onClick={() => setFilter('all')}
                    className={filter === 'all' ? 'gp-btn-primary py-1.5 px-3 text-xs' : 'gp-btn-ghost py-1.5 px-3 text-xs'}
                  >
                    All ({subscriptions.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilter('active')}
                    className={`py-1.5 px-3 text-xs rounded-md font-semibold transition-colors ${
                      filter === 'active' ? '' : 'gp-btn-ghost'
                    }`}
                    style={
                      filter === 'active'
                        ? { background: 'var(--gp-success)', color: '#fff' }
                        : undefined
                    }
                  >
                    Active ({activeCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilter('expired')}
                    className={`py-1.5 px-3 text-xs rounded-md font-semibold transition-colors ${
                      filter === 'expired' ? '' : 'gp-btn-ghost'
                    }`}
                    style={
                      filter === 'expired'
                        ? { background: '#b91c1c', color: '#fff' }
                        : undefined
                    }
                  >
                    Expired ({expiredCount})
                  </button>
                </div>
              </div>
            </div>
          </div>

          {filteredSubscriptions.length === 0 ? (
            <div className="gp-card text-center py-10">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ background: 'var(--gp-surface-muted)' }}
              >
                <FontAwesomeIcon icon={faCrown} className="text-2xl gp-text-muted" aria-hidden="true" />
              </div>
              <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--gp-ink)' }}>
                {filter === 'active' ? 'No Active Subscriptions' : 'No Subscriptions Found'}
              </h3>
              <p className="text-sm gp-text-muted mb-5">
                {filter === 'active'
                  ? "You don't have any active subscriptions. Browse our exams to get started!"
                  : searchQuery
                  ? 'No subscriptions match your search.'
                  : "You don't have any subscriptions yet. Browse our exams to get started!"}
              </p>
              <Link to="/certifications" className="gp-btn-primary inline-flex">
                <span>Browse Exams</span>
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {filteredSubscriptions.map((subscription) => {
                const status = getSubscriptionStatus(subscription);
                const daysRemaining = getDaysRemaining(subscription.end_date);
                const isExpiringSoon = daysRemaining > 0 && daysRemaining <= 30;
                const progressPercent = daysRemaining > 0
                  ? Math.min((daysRemaining / 365) * 100, 100)
                  : 0;

                const borderColor =
                  status.status === 'active'
                    ? 'var(--gp-success)'
                    : status.status === 'expiring'
                    ? 'var(--gp-accent)'
                    : 'var(--gp-border)';

                return (
                  <div
                    key={subscription.id}
                    className="gp-card overflow-hidden p-0"
                    style={{ borderColor, borderWidth: 2 }}
                  >
                    <div
                      className="p-4"
                      style={{
                        background:
                          status.status === 'active'
                            ? 'var(--gp-success-soft)'
                            : status.status === 'expiring'
                            ? 'var(--gp-accent-soft)'
                            : 'var(--gp-surface-muted)'
                      }}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <FontAwesomeIcon
                              icon={faGraduationCap}
                              className="text-sm shrink-0"
                              style={{
                                color:
                                  status.status === 'active'
                                    ? 'var(--gp-success)'
                                    : status.status === 'expiring'
                                    ? 'var(--gp-accent)'
                                    : 'var(--gp-ink-muted)'
                              }}
                              aria-hidden="true"
                            />
                            <h3 className="text-base font-bold truncate" style={{ color: 'var(--gp-ink)' }}>
                              {subscription.exam_name}
                            </h3>
                          </div>
                          {subscription.exam_description && (
                            <p className="text-xs gp-text-muted line-clamp-2">
                              {subscription.exam_description}
                            </p>
                          )}
                        </div>
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold shrink-0"
                          style={statusBadgeStyle(status.color)}
                        >
                          {status.label}
                        </span>
                      </div>

                      {status.status === 'active' && daysRemaining > 0 && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs gp-text-muted mb-1">
                            <span>Days Remaining</span>
                            <span className="font-semibold" style={{ color: 'var(--gp-ink)' }}>
                              {daysRemaining} days
                            </span>
                          </div>
                          <div
                            className="w-full rounded-full h-1.5"
                            style={{ background: 'var(--gp-border)' }}
                          >
                            <div
                              className="h-1.5 rounded-full transition-all"
                              style={{
                                width: `${progressPercent}%`,
                                background: isExpiringSoon ? 'var(--gp-accent)' : 'var(--gp-success)'
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[11px] gp-text-muted mb-0.5">Start Date</p>
                          <div className="flex items-center gap-1.5">
                            <FontAwesomeIcon icon={faCalendar} className="text-xs gp-text-muted" aria-hidden="true" />
                            <p className="text-sm font-medium" style={{ color: 'var(--gp-ink)' }}>
                              {formatDate(subscription.start_date)}
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-[11px] gp-text-muted mb-0.5">End Date</p>
                          <div className="flex items-center gap-1.5">
                            <FontAwesomeIcon icon={faCalendar} className="text-xs gp-text-muted" aria-hidden="true" />
                            <p className="text-sm font-medium" style={{ color: 'var(--gp-ink)' }}>
                              {formatDate(subscription.end_date)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {subscription.amount_paid != null && (
                        <div>
                          <p className="text-[11px] gp-text-muted mb-0.5">Amount Paid</p>
                          <p className="text-sm font-semibold" style={{ color: 'var(--gp-ink)' }}>
                            {formatCurrency(subscription.amount_paid, subscription.currency || 'USD')}
                          </p>
                        </div>
                      )}

                      {status.status === 'expiring' && (
                        <div
                          className="flex items-start gap-2 p-3 rounded-md"
                          style={{
                            background: 'var(--gp-accent-soft)',
                            border: '1px solid var(--gp-accent)'
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="mt-0.5"
                            style={{ color: 'var(--gp-accent)' }}
                            aria-hidden="true"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium" style={{ color: 'var(--gp-accent)' }}>
                              Expiring in {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}
                            </p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--gp-accent)' }}>
                              Renew your subscription to continue access
                            </p>
                          </div>
                        </div>
                      )}

                      <div
                        className="flex items-center gap-2 pt-3"
                        style={{ borderTop: '1px solid var(--gp-border)' }}
                      >
                        <Link
                          to={getExamUrl(subscription.exam_id, subscription.exam_name)}
                          className="gp-btn-primary flex-1 text-sm"
                        >
                          <span>View Exam</span>
                          <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                        </Link>
                        {(status.status === 'expired' || status.status === 'inactive') && (
                          <Link
                            to={`/subscription/${subscription.exam_id}`}
                            className="gp-btn-ghost text-sm"
                          >
                            Renew
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MySubscriptions;
